/**
 * Inline SVG icons. 20×20 viewBox, stroke-based (currentColor), 1.5px stroke.
 * Subset of POC1's icon set — port more as additional screens land.
 */
import type { SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

const Base = ({ size = 20, children, ...rest }: IconProps & { children: React.ReactNode }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 20 20"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...rest}
  >
    {children}
  </svg>
);

export const SaveIcon    = (p: IconProps) => <Base {...p}><path d="M14 3H4v14h14V7l-4-4z" /><path d="M6 3v4h8V3M8 13h4" /></Base>;
export const DeleteIcon  = (p: IconProps) => <Base {...p}><path d="M4 5h14M9 9v6M13 9v6M6 5l1 12h8l1-12M8 5V3h6v2" /></Base>;
export const PrintIcon   = (p: IconProps) => <Base {...p}><path d="M6 6V3h10v3M4 9h14v7h-3v2H7v-2H4z" /><path d="M7 14h8" /></Base>;
export const ExportIcon  = (p: IconProps) => <Base {...p}><path d="M10 3v11M6 9l4-4 4 4M3 17h14" /></Base>;
export const RefreshIcon = (p: IconProps) => <Base {...p}><path d="M16 8a6 6 0 1 0 0 4M16 4v4h-4" /></Base>;
// slice-3561 (BigList #24, ADR-0045): PB's quick_window.ico — "the rabbit". Toolbar item #2
// on every PowerHouse screen (m_phfc_main.srm:5309). A rabbit because Quick Window is the
// fast hop from the row you are on to any screen that can be reached from it.
export const RabbitIcon  = (p: IconProps) => (
  <Base {...p}>
    <path d="M7.6 9.4C6.3 6.9 6 4.4 6.8 3c.7-1.2 1.9-.3 2.2 1.9.2 1.4.2 2.9 0 4.1" />
    <path d="M12.4 9.4c1.3-2.5 1.6-5 .8-6.4-.7-1.2-1.9-.3-2.2 1.9-.2 1.4-.2 2.9 0 4.1" />
    <circle cx="10" cy="13.5" r="4" />
    <path d="M8.7 12.9h.01M11.3 12.9h.01" />
    <path d="M10 14.4l-.9 1M10 14.4l.9 1" />
  </Base>
);
export const HistoryIcon = (p: IconProps) => <Base {...p}><path d="M3 10a7 7 0 1 0 2-5" /><path d="M3 3v4h4" /><path d="M10 6v4l3 2" /></Base>;
export const SearchIcon  = (p: IconProps) => <Base {...p}><circle cx="9" cy="9" r="5" /><path d="M13 13l4 4" /></Base>;
// slice-3346 (#2936): PB icon_user — the "&Users in Group" float on w_phfc_grps_for_usr.
export const UserIcon    = (p: IconProps) => <Base {...p}><circle cx="10" cy="6.5" r="3" /><path d="M4.5 17c.9-3.2 2.9-4.8 5.5-4.8s4.6 1.6 5.5 4.8" /></Base>;
// #2065 (VAL): "Run Search" glyph distinct from the magnifier — the magnifier reads as the
// Zoom button on every other PH10 screen. Binoculars = PB's classic run-a-query metaphor.
export const RunSearchIcon = (p: IconProps) => (
  <Base {...p}>
    <circle cx="6" cy="13" r="3" />
    <circle cx="14" cy="13" r="3" />
    <path d="M9 12h2M5 10V5h2v5M13 10V5h2v5" />
  </Base>
);
export const CloseIcon   = (p: IconProps) => <Base {...p}><path d="M5 5l10 10M15 5L5 15" /></Base>;
export const NewIcon     = (p: IconProps) => <Base {...p}><path d="M10 3v14M3 10h14" /></Base>;
// Slice-336 (feedback #33 LINK): blank-page icon for the "New" toolbar action. PB 9.0
// uses a sheet-of-paper glyph for New (vs PH10's prior plus-sign NewIcon). Folded top-
// right corner hints "fresh document". Used by the generic crudActions Add button so every
// browse with Add CRUD enabled gets the PB-faithful icon+label.
export const NewPageIcon = (p: IconProps) => (
  <Base {...p}>
    <path d="M5 2h7l3 3v13H5z" />
    <path d="M12 2v3h3" />
  </Base>
);
export const DocumentIcon = (p: IconProps) => <Base {...p}><path d="M12 2H5a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V6l-4-4z" /><path d="M12 2v4h4M7 10h6M7 13h6M7 16h4" /></Base>;
export const CommentIcon = (p: IconProps) => <Base {...p}><path d="M3 5a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H8l-4 3v-3H4a1 1 0 0 1-1-1V5z" /></Base>;
export const ApproveIcon = (p: IconProps) => <Base {...p}><path d="M4 11l4 4 8-9" /></Base>;
// slice-3482 (#3043, KATHY): PB registers icon_confirm_all_items (ue_confirm_all_items,
// "Confirm All" — a scan-all-lines fill) as a DISTINCT .ico asset from
// icon_confirm_work_all (ue_pack_all, "Pack All" — the carton-count prompt loop), and
// w_multi_src_pickpack_ship is the one screen with BOTH on its toolbar. A double
// checkmark keeps it visually related to ApproveIcon (single check, used elsewhere for
// plain Confirm All) while staying distinguishable next to Pack All on the same bar.
export const ConfirmAllIcon = (p: IconProps) => <Base {...p}><path d="M2 10l3 3 6-7" /><path d="M9 11l3 3 6-7" /></Base>;
// slice-3485 (#3046, ROGER): PB icon_select (uo_phfc_icons.sru:115, '.\icons\phfc\select.ico')
// -- the toolbar icon w_rep_cyc_choose.srw registers via wf_menu_new("Run", ..., icon_select,
// "ue_run", "&Select") alongside its cb_1 "&Select" push button (srw:1052-1063, both fire the
// same dw_1::ue_run). A classic pointer/selection-tool arrow, generic to any PB chooser popup.
export const SelectIcon = (p: IconProps) => (
  <Base {...p}>
    <path d="M4 3v13l3.2-3.1L9.3 17l1.8-.9-2.1-4.1H13z" />
  </Base>
);
export const ReceiveIcon = (p: IconProps) => <Base {...p}><path d="M3 7l7-3 7 3v6l-7 3-7-3V7z" /><path d="M3 7l7 3 7-3M10 10v7" /></Base>;
export const LabelIcon   = (p: IconProps) => <Base {...p}><path d="M2 4h8l7 7-7 7-7-7V4z" /><circle cx="6.5" cy="7.5" r="1" /></Base>;
export const DollarIcon  = (p: IconProps) => <Base {...p}><path d="M10 3v14M13 6H8a2 2 0 0 0 0 4h4a2 2 0 0 1 0 4H7" /></Base>;
export const ClassicIcon = (p: IconProps) => (
  // Vintage Win32 window outline — used for Order Inquiry's "Sister Orders" button.
  <Base {...p}>
    <rect x="2" y="3" width="16" height="13" rx="0.5" />
    <path d="M2 6h16M14 4.5h2M11 4.5h2" />
  </Base>
);
export const StarIcon = (p: IconProps & { filled?: boolean }) => {
  const { filled, ...rest } = p;
  return (
    <Base {...rest}>
      <path
        d="M10 3l2.4 4.9 5.4.8-3.9 3.8.9 5.4L10 15.4 5.2 17.9l.9-5.4L2.2 8.7l5.4-.8L10 3z"
        fill={filled ? 'currentColor' : 'none'}
      />
    </Base>
  );
};
export const ChevronLeftIcon = (p: IconProps) => <Base {...p}><path d="M12 4l-6 6 6 6" /></Base>;
export const ChevronRightIcon = (p: IconProps) => <Base {...p}><path d="M8 4l6 6-6 6" /></Base>;
// slice-3651: drag handle for reorderable lists (Sidebar Favorites) — two columns of dots.
export const GripIcon = (p: IconProps) => (
  <Base {...p} strokeWidth={0}>
    {[5, 10, 15].map((y) => (
      <g key={y}>
        <circle cx="7.5" cy={y} r="1.4" fill="currentColor" />
        <circle cx="12.5" cy={y} r="1.4" fill="currentColor" />
      </g>
    ))}
  </Base>
);
// slice-3130: PB icon_move_up / icon_move_down (w_interface_services_2 Move Up / Move Down).
export const MoveUpIcon = (p: IconProps) => <Base {...p}><path d="M10 16V5M5 9l5-5 5 5" /></Base>;
export const MoveDownIcon = (p: IconProps) => <Base {...p}><path d="M10 4v11M5 11l5 5 5-5" /></Base>;
// slice-3139: PB icon_menu_expand / icon_menu_collapse (w_rf_profile_det Expand All /
// Collapse All, srw:58-59) — double chevrons opening / closing the tree.
export const ExpandAllIcon = (p: IconProps) => <Base {...p}><path d="M5 4l5 4 5-4" /><path d="M5 11l5 4 5-4" /></Base>;
export const CollapseAllIcon = (p: IconProps) => <Base {...p}><path d="M5 8l5-4 5 4" /><path d="M5 16l5-4 5 4" /></Base>;
export const InfoIcon = (p: IconProps) => <Base {...p}><circle cx="10" cy="10" r="7" /><path d="M10 9v4M10 6.5v.5" /></Base>;
export const EditIcon = (p: IconProps) => <Base {...p}><path d="M14 4l2 2-9 9H5v-2z" /><path d="M12 6l2 2" /></Base>;
export const CloneIcon = (p: IconProps) => <Base {...p}><path d="M6 2h8a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z" /><path d="M3 6v11a1 1 0 0 0 1 1h9" /></Base>;
// PB icon_lock_location (lock_location.ico) — the w_contents_maint Unlock/Lock Location key.
// Two web states: closed padlock = locked-by-me, open shackle = unlocked.
export const LockIcon   = (p: IconProps) => <Base {...p}><rect x="5" y="9" width="10" height="8" rx="1" /><path d="M7 9V6a3 3 0 0 1 6 0v3" /><path d="M10 12v2" /></Base>;
export const UnlockIcon = (p: IconProps) => <Base {...p}><rect x="5" y="9" width="10" height="8" rx="1" /><path d="M7 9V6a3 3 0 0 1 5.6-1.5" /><path d="M10 12v2" /></Base>;
export const LabelRangeIcon = (p: IconProps) => <Base {...p}><path d="M2 5h6l5 5-5 5-6-5V5z" /><path d="M9 5h6l5 5-5 5-6-5" /><circle cx="5" cy="8" r=".75" /></Base>;
// Slice-329 (feedback #10): scissors cutting a sheet of paper. PB w_order_search uses
// icon_order_split for the Order Split toolbar action; the LabelRangeIcon we were using
// looked nothing like the PB scissors. Two finger holes + closed blades meeting at the
// cutting tip + a sheet of paper crossing horizontally through the cut.
export const ScissorsIcon = (p: IconProps) => (
  <Base {...p}>
    {/* paper */}
    <path d="M2 10h16" />
    {/* upper blade */}
    <path d="M10 10L4 5" />
    {/* lower blade */}
    <path d="M10 10L4 15" />
    {/* finger holes */}
    <circle cx="4.5" cy="5.5" r="1.5" />
    <circle cx="4.5" cy="14.5" r="1.5" />
  </Base>
);
// Slice-329 (feedback #5): "Fit columns" toolbar action -- two horizontal arrows pointing
// inward toward a vertical bar, signifying "shrink to fit content".
export const FitColumnsIcon = (p: IconProps) => (
  <Base {...p}>
    <path d="M3 10h4M17 10h-4" />
    <path d="M5 8l2 2-2 2M15 8l-2 2 2 2" />
    <path d="M10 4v12" />
  </Base>
);
// Slice-329 (feedback #3): "Adjust Inventory" toolbar action -- a box (inventory) with a
// small wrench overlay. PB uses icon_adjust on w_item_status's "tree" toolbar action.
export const AdjustInventoryIcon = (p: IconProps) => (
  <Base {...p}>
    {/* box */}
    <path d="M3 7l7-3 7 3v7l-7 3-7-3V7z" />
    <path d="M3 7l7 3 7-3M10 10v7" />
    {/* small wrench overlay */}
    <path d="M14 12l3 3M14 12a1.5 1.5 0 1 1 3 0" />
  </Base>
);
// slice-3060 (#2209, CHRIS+ROGER 07-23): "Adjust Lost Inventory" -- PB registers this as
// icon_lost_adjust (w_item_status.srw:327), a DIFFERENT icon asset from icon_adjust
// (AdjustInventoryIcon above, srw:325). PH10 had both toolbar buttons sharing
// AdjustInventoryIcon's glyph -- visually identical apart from the label, which is how a
// grayed-out "Adjust Inventory" next to an enabled "Adjust Lost Inventory" (or vice versa)
// reads as "the wrong icon" at a glance. Same box, but a "?" overlay (inventory whose
// whereabouts are unknown/lost) instead of the wrench, so the two are tellable apart.
export const AdjustLostInventoryIcon = (p: IconProps) => (
  <Base {...p}>
    {/* box */}
    <path d="M3 7l7-3 7 3v7l-7 3-7-3V7z" />
    <path d="M3 7l7 3 7-3M10 10v7" />
    {/* "?" overlay marking the contents as lost/unaccounted */}
    <circle cx="15" cy="6" r="4.25" fill="currentColor" stroke="none" opacity="0.15" />
    <path d="M13.6 5.1a1.4 1.4 0 1 1 2 1.3c-.5.3-.9.6-.9 1.2" />
    <path d="M14.7 8.6v.1" />
  </Base>
);
// Slice-330 (feedback #21): "Generate RMA" PB icon_generate_rma -- a dog-eared paper on
// bottom, with a purple arrow pointing left and a yellow carton-box above it. The
// carton+arrow signal "send something back to the warehouse" (return); the document is
// the RMA paperwork being generated.
//
// Multi-color via per-path stroke/fill overrides (most icons in this file are monochrome
// using currentColor; PB's colorful action icons are an exception ROGER specifically
// asked us to preserve so operators can scan the toolbar by color memory).
export const GenerateRmaIcon = (p: IconProps) => (
  <Base {...p}>
    {/* yellow carton at top-left */}
    <path d="M2 3h5v4H2z" fill="#fbbf24" stroke="#92400e" />
    <path d="M2 3l2.5 1.5L7 3" stroke="#92400e" />
    {/* purple arrow pointing left (return direction) */}
    <path d="M17 5h-5M14 3l-2 2 2 2" stroke="#7c3aed" strokeWidth={2} />
    {/* dog-eared paper (RMA document) at bottom */}
    <path d="M5 11h7l2 2v5H5z" />
    <path d="M12 11v2h2" />
  </Base>
);
// Slice-330 (feedback #22): "Generate Receipt" PB icon_receipt_from_order -- a clipboard
// with a small green dog-eared paper on top + a red triangle to the right pointing left.
// The clipboard is the receipt being generated; the green paper hints "from order"; the
// red triangle is the "from -> to" direction arrow.
export const GenerateReceiptIcon = (p: IconProps) => (
  <Base {...p}>
    {/* clipboard */}
    <path d="M4 4h7v13H4z" />
    <path d="M6 4V3h3v1" />
    <path d="M6 8h3M6 11h3M6 14h2" />
    {/* small green dog-eared paper at top-left */}
    <path d="M2 2h3v3l-1 1-2-1z" fill="#86efac" stroke="#166534" />
    {/* red triangle pointing left */}
    <path d="M17 9l-3 2 3 2z" fill="#ef4444" stroke="#991b1b" />
  </Base>
);

// Wrench / spanner — used for "Item Configuration" (PB caption). 14° tilt mimics the
// vintage Win32 toolbar wrench. The open-end jaw is at the upper-right; handle runs
// diagonal to the lower-left with a small grip notch at the butt.
export const WrenchIcon = (p: IconProps) => (
  <Base {...p}>
    <path d="M13.5 3.5a3.5 3.5 0 0 0-4.6 4.6l-5.4 5.4a1.5 1.5 0 0 0 0 2.1l.9.9a1.5 1.5 0 0 0 2.1 0l5.4-5.4a3.5 3.5 0 0 0 4.6-4.6L14.5 8.5 12 8l-.5-2.5 2-2z" />
  </Base>
);

// Maintenance — wrench + gear cog. PB's icon_maintenance is the classic "settings/
// maintenance" Win32 toolbar glyph. FB#268 (LINK) asked for the PB-faithful version
// after the slice-336 'tools' substitution didn't match. The wrench reads as the
// hand action, the gear reads as "settings" -- maintenance = "edit this row's
// configuration".
export const MaintenanceIcon = (p: IconProps) => (
  <Base {...p}>
    {/* Gear cog at upper-right -- 8 teeth around a hub. */}
    <circle cx="13.5" cy="6.5" r="2.3" />
    <path d="M13.5 3v-1.2 M13.5 10v1.2 M9.8 6.5h-1.2 M17.2 6.5h1.2
             M11 4l-.9-.9 M16 9l.9.9 M16 4l.9-.9 M11 9l-.9.9" />
    {/* Wrench at lower-left: open-end jaw at upper-right of the wrench, handle
        runs diagonal down to the lower-left corner. Mirrors WrenchIcon but
        positioned to avoid overlap with the gear. */}
    <path d="M8.5 8.5a3 3 0 0 0-3.9 3.9l-2.4 2.4a1 1 0 0 0 0 1.4l.6.6a1 1 0 0 0 1.4 0l2.4-2.4a3 3 0 0 0 3.9-3.9L9 11l-2-.3L7 8l1.5-1.5z" />
  </Base>
);

// Lined paper + snowflake -- "Change Activity Date" (FB#54 LINK). Half-sheet of
// lined paper at the bottom of the icon with horizontal rule lines; small 6-spoke
// snowflake sitting above the paper's top-right corner. The snowflake signals
// "freeze / re-stamp the date" -- a date snapshot being adjusted.
export const ChangeActivityDateIcon = (p: IconProps) => (
  <Base {...p}>
    {/* Lined paper at the bottom (~3 to ~13 vertically). */}
    <path d="M3 8h10v9H3z" />
    <path d="M5 11h6M5 13h6M5 15h4" />
    {/* Snowflake at top-right (~15, 2-7). Six rays from a central point. */}
    <path d="M15 2v6M12 5h6M13 3l4 4M17 3l-4 4" />
  </Base>
);

// Window frame + gear -- "Lotable Fields" (FB#55 LINK). Outer frame is a slim
// rectangle with a mullion at top; a 6-tooth gear sits centered inside the
// window. LINK described it as light-blue frame + green gear; the shared icon
// chassis paints monochrome via the caller's stroke color, so per-path tint
// (blue frame, green gear) would need a Base refactor we defer.
export const LotableFieldsIcon = (p: IconProps) => (
  <Base {...p}>
    {/* Window frame -- outer rectangle with mullion across the top quarter. */}
    <path d="M3 4h14v13H3z" />
    <path d="M3 7h14" />
    {/* Gear -- 6-tooth, center ~10,12. Outer teeth via short radial spokes;
        inner circle is the gear body; tiny center for the axle. */}
    <circle cx="10" cy="12" r="2.5" />
    <circle cx="10" cy="12" r="0.6" />
    <path d="M10 9v-1M10 16v-1M7 12H6M14 12h-1M7.9 9.9l-0.7-0.7M12.8 14.8l-0.7-0.7M7.9 14.1l-0.7 0.7M12.8 9.2l-0.7 0.7" />
  </Base>
);

// Window frame + lined paper inside -- "Expert Criteria" (FB#56 LINK). Same
// window frame as LotableFieldsIcon but with a half-sheet of lined paper
// (~3 horizontal rules) sitting inside instead of a gear. Reads as "advanced
// search criteria form in a popup window".
export const ExpertCriteriaIcon = (p: IconProps) => (
  <Base {...p}>
    {/* Window frame -- outer rectangle with mullion across the top quarter. */}
    <path d="M3 4h14v13H3z" />
    <path d="M3 7h14" />
    {/* Lined half-sheet of paper inside the window (~5,9 to ~15,16). */}
    <path d="M5 9h10v7H5z" />
    <path d="M7 11h6M7 13h6M7 15h4" />
  </Base>
);

// Pencil + $ -- "Create a Billing Tran" (FB#53 LINK). Pencil runs diagonally from
// top-right (eraser) to bottom-left (point). $ sign sits above-and-left of the
// pencil point. PB's m_create_billing toolbar icon read the same way: financial
// action being authored.
export const BillingTranIcon = (p: IconProps) => (
  <Base {...p}>
    {/* PENCIL body -- diagonal from top-right (~16,4) to lower-left (~7,13).
        Two parallel strokes form the shaft; a small triangle at the tip; a
        rectangular eraser block on the top end. */}
    <path d="M14 3l4 4-9 9-4-4 9-9z" />
    <path d="M5 12l4 4-5 1 1-5z" />
    <path d="M13 4l4 4" />
    {/* $ sign -- positioned above-and-left of the pencil point (~3-6, 2-7) so it
        reads as "money" associated with the pencil's writing action. Compact 'S' +
        vertical bar through it. */}
    <path d="M4 3v6M2.5 4.5h3M2.5 7.5h3M5.5 4.5a1.2 1.2 0 0 0-1.5-1.2 1.2 1.2 0 0 0 0 2.4 1.2 1.2 0 0 1 0 2.4 1.2 1.2 0 0 1-1.5-1.2" />
  </Base>
);

// Crossed hammer + wrench -- "Item Maintenance" (FB#37 LINK, redrawn FB#48). Handles
// meet at center (~10,10), heads splay to the corners forming an X. Wrench: handle
// runs NW-to-center, open-jaw at top-left (~3,3). Hammer: handle runs SE-from-center,
// head at bottom-right (~17,17). The two handles overlap at the middle.
export const ToolsIcon = (p: IconProps) => (
  <Base {...p}>
    {/* WRENCH: open jaw at top-left, handle running diagonally down-right toward center.
        Jaw is the "C" shape; handle is the line from jaw to (~10,10). */}
    <path d="M5 3a2.5 2.5 0 0 0-1.4 4.2l1.2 1.2L8 5.2 6.8 4A2.5 2.5 0 0 0 5 3z" />
    <path d="M5 8.5l5 5" />
    {/* HAMMER: head at bottom-right, handle running diagonally up-left toward center.
        Head is the small rectangle; handle is the line from head to (~10,10). */}
    <path d="M15.5 17a1.5 1.5 0 0 0 2.1-2.1l-2.4-2.4-2.1 2.1 2.4 2.4z" />
    <path d="M13.1 14.6l-5-5" />
    {/* The two handle lines visually cross at (~9.5, 9.5). Heads at NW + SE corners. */}
  </Base>
);

// Stop / hold hand — open palm facing viewer. Used for "Item Hold" (PB caption).
// Four finger lines + thumb on the left; rounded palm at the bottom for a friendly
// rather than aggressive feel.
export const HandIcon = (p: IconProps) => (
  <Base {...p}>
    <path d="M7 9V4.5a1 1 0 0 1 2 0V9" />
    <path d="M9 9V3.5a1 1 0 0 1 2 0V9" />
    <path d="M11 9V4a1 1 0 0 1 2 0v5" />
    <path d="M13 9V6a1 1 0 0 1 2 0v6a5 5 0 0 1-5 5h-1a5 5 0 0 1-4.5-2.8L3 11a1 1 0 0 1 1.7-1L7 12" />
  </Base>
);

// Colored info — same circle-with-i as InfoIcon but the circle is filled with a soft
// blue tint to convey "richer info" (used for "Style Color Status" — PB caption). The
// stroke stays currentColor so it still inherits text color in disabled state, but
// the fill flags it visually different from plain Item Status / Info.
// Slice-336 (feedback #35 LINK): Style Color Status -- small "i" inside a rainbow circle.
// PB 9.0 uses a multi-band circle (style/color hint) with the info "i" centered. We render
// four colored quadrants approximating the rainbow + the "i" overlay. {@code SVGProps.id}
// is honored on the linearGradient via the icon's instance id so multiple uses on one page
// don't clash (each invocation uses a deterministic id from React's useId-equivalent).
export const InfoColorIcon = (p: IconProps) => {
  // Stable per-mount id so multiple icons on the same page don't reuse a gradient ref.
  // Using Math.random would re-key the gradient every render and cause SVG flicker; we
  // accept a single random per ICON instance via the component identity (closures over
  // module-scope Map keyed by parent props isn't reliable). Practical fix: derive from
  // the className when present (icons usually get a className), fallback to a constant.
  const id = `info-color-grad-${p.className ?? 'default'}`;
  return (
    <Base {...p}>
      <defs>
        <linearGradient id={id} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#ef4444" />
          <stop offset="33%" stopColor="#fbbf24" />
          <stop offset="66%" stopColor="#22c55e" />
          <stop offset="100%" stopColor="#3b82f6" />
        </linearGradient>
      </defs>
      <circle cx="10" cy="10" r="7" fill={`url(#${id})`} stroke="#475569" strokeWidth="0.75" />
      {/* white "i" centered for high contrast against the rainbow */}
      <path
        d="M10 9v4M10 6.5v.5"
        stroke="#ffffff"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </Base>
  );
};

// Chain-link — used for "Copy Share Link" toolbar action. Two interlocking ovals oriented
// diagonally so they read as a link even at 20px.
export const LinkIcon = (p: IconProps) => (
  <Base {...p}>
    <path d="M8.5 11.5l3-3" />
    <path d="M6 13a3 3 0 0 1 0-4l2-2a3 3 0 0 1 4 0" />
    <path d="M14 7a3 3 0 0 1 0 4l-2 2a3 3 0 0 1-4 0" />
  </Base>
);

// Envelope — used for "Email Share Link" toolbar action. Outer rectangle + flap chevron.
export const MailIcon = (p: IconProps) => (
  <Base {...p}>
    <rect x="3" y="5" width="14" height="10" rx="1" />
    <path d="M3 6l7 5 7-5" />
  </Base>
);

// ─── Slice-366: PB-faithful icons for Tag Order Search (w_order_tag_search) ─────────
// Drawn to mirror PB's icon_tag_all / icon_untag_all / icon_wave_* / icon_pallet_build /
// icon_soft_allocate / icon_order_master so operators recognize the toolbar from PB
// muscle memory. The icons are monochrome (currentColor) like the rest of the set;
// PB's are colorful but the silhouettes are recognizable.

// Three stacked tags fanning down-right -- "Tag" (PB icon_tag_all on w_order_tag_search).
export const TagAllIcon = (p: IconProps) => (
  <Base {...p}>
    <path d="M3 4h5l8 8-5 5-8-8V4z" />
    <path d="M5 6h3l7 7-3 3-7-7" />
    <path d="M7 8h3l6 6-2 2-6-6" />
    <circle cx="5" cy="6" r="0.6" />
  </Base>
);

// Single tag with an X overlay -- "Untag" (PB icon_untag_all). The X reads as "remove".
export const UntagAllIcon = (p: IconProps) => (
  <Base {...p}>
    <path d="M2 4h6l10 10-6 6-10-10V4z" />
    <circle cx="5" cy="7" r="0.75" />
    <path d="M11 11l6 6M17 11l-6 6" strokeWidth={2} />
  </Base>
);

// ─── Chris 2026-08-06 ("Make the icons as close to PB but PH10 vibed as possible") ───
//
// PB's Carton/Pallet Loading toolbar registers FIVE distinct .ico assets
// (`w_truck_load_carton_to.srw:501-505` → `uo_phfc_icons.sru:122 tag_all.ico`,
// `:201 untag_selected.ico`, `:126 tag_selected.ico`, `:197 untag_all.ico`, and
// `uo_icons.sru:323 truck_load.ico`). PH10 was drawing ApproveIcon twice and CloseIcon twice,
// so Tag/Tag-selected and Untag/Untag-selected were the same picture — indistinguishable at a
// glance, which is precisely the complaint. slice-3246 fixed the captions; these are the
// pictures.
//
// The .ico binaries are not in the export (`find … -iname 'tag_all.ico'` finds nothing), so
// these are drawn from the asset NAMES and PB's semantics, not traced. The family rule:
//
//   * "all"      → the TAG silhouette (TagAllIcon's fan of three, UntagAllIcon's tag + X)
//   * "selected" → a ROW LIST with one row boxed — because that is literally what the button
//                  does: it acts on the rows the operator has highlighted, not on every row.
//
// Two unrelated silhouettes rather than four near-identical tags means the toolbar reads at a
// glance even at 20px, while Tag-vs-Untag stays the universal check-vs-cross inside each pair.
// Same Base, same 1.5 stroke, same 20×20 viewBox as everything above.

// Row list with the middle row boxed and ticked -- "Tag selected" (PB icon_tag_selected).
export const TagSelectedIcon = (p: IconProps) => (
  <Base {...p}>
    <path d="M3 5h14" />
    <rect x="2" y="7.5" width="16" height="5" rx="1" />
    <path d="M3 15h14" />
    <path d="M4.6 10.1l1.5 1.5 3-3" />
    <path d="M11.5 10h5" />
  </Base>
);

// Row list with the middle row boxed and crossed -- "Untag selected" (PB icon_untag_selected).
export const UntagSelectedIcon = (p: IconProps) => (
  <Base {...p}>
    <path d="M3 5h14" />
    <rect x="2" y="7.5" width="16" height="5" rx="1" />
    <path d="M3 15h14" />
    <path d="M4.6 8.6l3 3M7.6 8.6l-3 3" />
    <path d="M11.5 10h5" />
  </Base>
);

// Box dropping INTO the trailer -- "Load Carton" (PB icon_truck_load, uo_icons.sru:323).
// Box above the bed, arrow pointing down at it: cargo going on.
export const TruckLoadIcon = (p: IconProps) => (
  <Base {...p}>
    <rect x="1.5" y="9" width="9" height="5.5" rx="0.5" />
    <path d="M10.5 10.5h3l3 3v1h-3" />
    <circle cx="5" cy="16.5" r="1.4" />
    <circle cx="13.5" cy="16.5" r="1.4" />
    <rect x="4" y="1.5" width="4.5" height="4.5" rx="0.5" />
    <path d="M6.25 6v2.5M4.5 7l1.75 1.5L8 7" />
  </Base>
);

// Box lifting OUT of the trailer -- "Unload Carton" (w_truck_unload_carton_from.srw:202).
// Mirror of TruckLoadIcon with the arrow reversed; see the note in TruckUnloadCartonFromPage
// about PB registering icon_truck_load on BOTH windows.
export const TruckUnloadIcon = (p: IconProps) => (
  <Base {...p}>
    <rect x="1.5" y="9" width="9" height="5.5" rx="0.5" />
    <path d="M10.5 10.5h3l3 3v1h-3" />
    <circle cx="5" cy="16.5" r="1.4" />
    <circle cx="13.5" cy="16.5" r="1.4" />
    <rect x="4" y="1.5" width="4.5" height="4.5" rx="0.5" />
    <path d="M6.25 8.5V6M4.5 7.5l1.75-1.5L8 7.5" />
  </Base>
);

// Wave + plus -- "Create New Wave" (PB icon_wave_new). The wave is a sine curve; plus
// signifies create.
export const WaveNewIcon = (p: IconProps) => (
  <Base {...p}>
    <path d="M2 12c2-4 4-4 6 0s4 4 6 0 4-4 6 0" />
    <path d="M14 4v6M11 7h6" strokeWidth={1.8} />
  </Base>
);

// Wave + info circle -- "Wave Status" (PB icon_wave_status). The wave is the same sine
// curve; the small info circle says "tell me about this".
export const WaveStatusIcon = (p: IconProps) => (
  <Base {...p}>
    <path d="M2 14c2-4 4-4 6 0s4 4 6 0 4-4 6 0" />
    <circle cx="15" cy="5" r="3" />
    <path d="M15 4.5v2.5M15 3.5v.5" />
  </Base>
);

// Wave + right-arrow -- "Submit Wave" (PB icon_wave). The arrow signifies "fire it off".
export const WaveSubmitIcon = (p: IconProps) => (
  <Base {...p}>
    <path d="M2 11c2-4 4-4 6 0s4 4 6 0" />
    <path d="M14 11l4 0M16 9l2 2-2 2" strokeWidth={2} />
  </Base>
);

// Stacked pallet + plus -- "Pallet Build For Wave" (PB icon_pallet_build).
export const PalletBuildIcon = (p: IconProps) => (
  <Base {...p}>
    {/* Pallet base (3 horizontal slats) */}
    <path d="M2 15h12M2 17h12" />
    <path d="M3 15v2M7 15v2M11 15v2M13 15v2" />
    {/* Boxes on the pallet */}
    <path d="M3 11h4v4H3zM7 9h4v6H7z" />
    {/* Plus -- "build" */}
    <path d="M16 5v4M14 7h4" strokeWidth={1.8} />
  </Base>
);

// ─── slice-2898: distinct glyphs for the remaining w_order_tag_search toolbar icons ──
// PB assigns a DISTINCT icon per wf_menu_new (w_order_tag_search.srw:914-942):
// icon_wave_assign / icon_wave_unassign / icon_wave_multi / icon_wave_multi_submit /
// icon_window_statistics. PH10 previously collapsed these onto shared glyphs
// (assign+unassign -> document, multiwave -> wave-new, submit-multi -> wave-submit);
// now each action reads distinctly, same monochrome style as the rest of the set.

// Wave + down-arrow into it -- "Assign to Wave" (PB icon_wave_assign): orders drop INTO
// the wave.
export const WaveAssignIcon = (p: IconProps) => (
  <Base {...p}>
    <path d="M2 14c2-4 4-4 6 0s4 4 6 0 4-4 6 0" />
    <path d="M10 2v7M7 6l3 3 3-3" strokeWidth={1.8} />
  </Base>
);

// Wave + up-arrow out of it -- "Unassign" (PB icon_wave_unassign): orders lift OUT of
// the wave (mirror of WaveAssignIcon).
export const WaveUnassignIcon = (p: IconProps) => (
  <Base {...p}>
    <path d="M2 14c2-4 4-4 6 0s4 4 6 0 4-4 6 0" />
    <path d="M10 9V2M7 5l3-3 3 3" strokeWidth={1.8} />
  </Base>
);

// Double wave + plus -- "Multi Wave" (PB icon_wave_multi): two stacked sine curves say
// "several waves"; plus signifies create (cf. WaveNewIcon's single curve).
export const WaveMultiIcon = (p: IconProps) => (
  <Base {...p}>
    <path d="M2 12c2-4 4-4 6 0s4 4 6 0" />
    <path d="M2 16c2-4 4-4 6 0s4 4 6 0" />
    <path d="M15 4v6M12 7h6" strokeWidth={1.8} />
  </Base>
);

// Double wave + right-arrow -- "Submit Multi-Wave" (PB icon_wave_multi_submit): the
// two-curve stack of WaveMultiIcon with WaveSubmitIcon's fire-it-off arrow.
export const WaveMultiSubmitIcon = (p: IconProps) => (
  <Base {...p}>
    <path d="M2 9c2-4 4-4 6 0s4 4 6 0" />
    <path d="M2 14c2-4 4-4 6 0s4 4 6 0" />
    <path d="M14 12h4M16 10l2 2-2 2" strokeWidth={2} />
  </Base>
);

// Bar chart -- "Show Statistics" (PB icon_window_statistics): toggles the statistics
// panel (dw_statistics) below the grid.
export const WindowStatisticsIcon = (p: IconProps) => (
  <Base {...p}>
    <path d="M3 17h14" />
    <path d="M5 17v-5M9 17V7M13 17v-7M17 17V4" strokeWidth={1.8} />
  </Base>
);

// Dotted box with a downward arrow into it -- "Soft Allocate" (PB icon_soft_allocate).
// The dotted box reads as "tentative / soft"; the arrow says "allocate into".
export const SoftAllocateIcon = (p: IconProps) => (
  <Base {...p}>
    <path d="M3 10h14v8H3z" strokeDasharray="2 2" />
    <path d="M10 2v6M7 5l3 3 3-3" strokeWidth={1.8} />
  </Base>
);

// Clipboard with a star -- "Order Master" (PB icon_order_master). The star marks
// "master / canonical".
export const OrderMasterIcon = (p: IconProps) => (
  <Base {...p}>
    {/* Clipboard outline */}
    <path d="M5 4h10v14H5z" />
    <path d="M7 4V3h6v1" />
    {/* Star inside */}
    <path d="M10 7l1 2 2 .2-1.5 1.5.5 2-2-1-2 1 .5-2L7 9.2 9 9z" />
  </Base>
);

// Question mark inside a circle — used by ScreenHelpButton (slice-110). Renders the
// "what is this screen / show me PB source info" affordance in the far-right toolbar
// slot. Click opens a popover with the PB window name + current route + copy buttons.
export const HelpIcon = (p: IconProps) => (
  <Base {...p}>
    <circle cx="10" cy="10" r="7" />
    <path d="M8 8a2 2 0 1 1 2 2v1" />
    <circle cx="10" cy="13.5" r=".6" fill="currentColor" />
  </Base>
);

/** Form-view icon: a document outline with three labeled-field rows — used for the
 *  generic "Form View" toggle on browse screens (tester ROGER #509). */
export const FormViewIcon = (p: IconProps) => (
  <Base {...p}>
    <rect x="3" y="2" width="14" height="16" rx="1" />
    <path d="M6 6h8M6 9h5M6 12h8" />
  </Base>
);

// ── slice-3008 (#1874/#1972): PB w_labor_task task-state toolbar icons ─────────
// PB registers these via wf_menu_new with iuo_icons icon_run / icon_pause /
// icon_restart / icon_stop (w_labor_task.srw:170-173). Web equivalents follow the
// universal transport-control glyphs so operators recognize Start/Pause/Restart/Stop
// at a glance.

/** PB icon_run — Start (w_labor_task.srw:170). */
export const PlayIcon = (p: IconProps) => (
  <Base {...p}>
    <path d="M6 4l10 6-10 6V4z" />
  </Base>
);

/** PB icon_pause — Pause (w_labor_task.srw:171). */
export const PauseIcon = (p: IconProps) => (
  <Base {...p}>
    <path d="M6 4v12M14 4v12" />
  </Base>
);

/** PB icon_restart — Restart (w_labor_task.srw:172): circular arrow + play wedge. */
export const RestartIcon = (p: IconProps) => (
  <Base {...p}>
    <path d="M16 9a6 6 0 1 0-1.5 4.5" />
    <path d="M16 4v5h-5" />
  </Base>
);

/** PB icon_stop — Stop (w_labor_task.srw:173). */
export const StopIcon = (p: IconProps) => (
  <Base {...p}>
    <rect x="5" y="5" width="10" height="10" />
  </Base>
);

/** PB icon_hold_place — "Set/Adjust Hold Code" (w_contents_maint.srw:1652):
 *  stop-octagon + exclamation = place/adjust a hold. */
export const HoldPlaceIcon = (p: IconProps) => (
  <Base {...p}>
    <path d="M7 3h6l4 4v6l-4 4H7l-4-4V7l4-4z" />
    <path d="M10 6.5v4.5M10 13.5v.5" />
  </Base>
);

/** PB icon_hold_release — "Release Hold Code" (w_contents_maint.srw:1653):
 *  stop-octagon + check = hold released. */
export const HoldReleaseIcon = (p: IconProps) => (
  <Base {...p}>
    <path d="M7 3h6l4 4v6l-4 4H7l-4-4V7l4-4z" />
    <path d="M7 10l2.5 2.5L13.5 8" />
  </Base>
);

/** PB icon_po_view — "User Report Test" preview (w_report_types.srw:267): an eye =
 *  view/preview the current record's report. */
export const EyeIcon = (p: IconProps) => (
  <Base {...p}>
    <path d="M2 10s3-5 8-5 8 5 8 5-3 5-8 5-8-5-8-5z" />
    <circle cx="10" cy="10" r="2.5" />
  </Base>
);

/** PB icon_kit_build — #2210 "Kit Build" (w_contents_maint.srw:1667): two small boxes
 *  merging into one larger box, the visual inverse of {@link ScissorsIcon} (Kit Breakdown). */
export const KitBuildIcon = (p: IconProps) => (
  <Base {...p}>
    <path d="M3 4h5v5H3zM12 4h5v5h-5z" />
    <path d="M7 6.5h6" />
    <path d="M4 12h12v4H4z" />
    <path d="M10 9v3" />
  </Base>
);

/** PB icon_rewarehouse_submit — #2210 "Rewarehouse Submit" (w_contents_maint.srw:1661): a
 *  pallet box with a forward-submit arrow, distinct from the plain "Submit for Putaway"
 *  ({@link ApproveIcon}). */
export const RewarehouseSubmitIcon = (p: IconProps) => (
  <Base {...p}>
    <path d="M3 8l6-3 6 3v6l-6 3-6-3V8z" />
    <path d="M3 8l6 3 6-3" />
    <path d="M12 15l5-5M17 10h-4M17 10v4" />
  </Base>
);

/** PB icon_services — ADR-0032 "Transfer" (w_contents_maint.srw:1671): two database
 *  cylinders with a directional arrow between them, distinguishing this cross-DATABASE
 *  move from the plain in-database {@link LinkIcon} ("Move"). */
export const TransferIcon = (p: IconProps) => (
  <Base {...p}>
    <path d="M2 4.5c0-.83 1.34-1.5 3-1.5s3 .67 3 1.5-1.34 1.5-3 1.5-3-.67-3-1.5z" />
    <path d="M2 4.5V9c0 .83 1.34 1.5 3 1.5s3-.67 3-1.5V4.5" />
    <path d="M2 6.75c0 .83 1.34 1.5 3 1.5s3-.67 3-1.5" />
    <path d="M12 11.5c0-.83 1.34-1.5 3-1.5s3 .67 3 1.5-1.34 1.5-3 1.5-3-.67-3-1.5z" />
    <path d="M12 11.5V16c0 .83 1.34 1.5 3 1.5s3-.67 3-1.5v-4.5" />
    <path d="M12 13.75c0 .83 1.34 1.5 3 1.5s3-.67 3-1.5" />
    <path d="M8 7l3 3M11 7l-3 3" />
  </Base>
);

/** PB icon_items_not_returnable (w_customer_shipto.srw:341 "&Not Returnable Item") — a
 *  return arrow with a no-entry slash through it. slice-3093 (#2148). */
export const NotReturnableIcon = (p: IconProps) => (
  <Base {...p}>
    <path d="M6 6a6 6 0 1 0 8.49 8.49" />
    <path d="M14.5 5.5L17 3M17 8V4h-4" />
    <path d="M3 17L17 3" />
  </Base>
);

/** slice-3098 (ITEM 1, #2256/#2257/#2267/#2274): PB icon_tote_location — Location
 *  Maintenance's "&Totes" toolbar icon (w_location.srw:27). An open-top tote box. */
export const ToteIcon = (p: IconProps) => (
  <Base {...p}>
    <path d="M4 7l1.5-3h9L16 7" />
    <path d="M4 7h12v9H4z" />
    <path d="M4 10.5h12" />
  </Base>
);

/** slice-3098: PB icon_min_max — Location Maintenance's "&Min/Max" toolbar icon
 *  (w_location.srw:28). Up/down bracket arrows around a bar, echoing the shared
 *  Min/Max tab metaphor used on the Item Master screen. */
export const MinMaxIcon = (p: IconProps) => (
  <Base {...p}>
    <path d="M6 5l4-3 4 3" />
    <path d="M10 2v7" />
    <path d="M6 15l4 3 4-3" />
    <path d="M10 11v7" />
  </Base>
);

/** slice-3098: PB icon_status — Location Maintenance's "&Status" toolbar icon
 *  (w_location.srw:29). A gauge/dial glyph. */
export const LocationStatusIcon = (p: IconProps) => (
  <Base {...p}>
    <circle cx="10" cy="11" r="6.5" />
    <path d="M10 11l3-3" />
    <path d="M7 4.5V3M13 4.5V3" />
  </Base>
);

/** slice-3431 (#2467): PB icon_timer — the RF Mover Screen mirror's "&Auto Refresh" toolbar
 *  item (w_rfdriver_screen.srw:1088), which arms a 1-second window timer. A stopwatch glyph. */
export const TimerIcon = (p: IconProps) => (
  <Base {...p}>
    <circle cx="10" cy="11.5" r="6" />
    <path d="M10 8.5v3l2 1.5" />
    <path d="M8 2.5h4" />
    <path d="M10 2.5v3" />
  </Base>
);
